import React, {useCallback, useEffect, useRef, useState} from 'react';
import './App.css';
import {BoardState, Position} from './model/BoardState';
import {Alert, Box, IconButton, useMediaQuery} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Game from './model/Game';
import ComputerCapturedPieces from "./view/ComputerCapturedPieces";
import PlayerCapturedPieces from "./view/PlayerCapturedPieces";
import {ComputerDirector} from './strategy/ComputerDirector';
import VintageChessBoard from "./view/VintageChessBoard";
import StartScreen from "./screen/StartScreen";
import GameOverScreen from "./screen/GameOverScreen";
import SettingsIcon from '@mui/icons-material/Settings';
import GameSettings from "./screen/GameSettings";
import StandardChessBoard from './view/StandardChessBoard';
import MobileStandardChessBoard from './view/MobileStandardChessBoard';
import { triggerComputerMoveAnimation as standardTriggerComputerMoveAnimation } from './view/StandardChessBoard';
import { triggerComputerMoveAnimation as mobileTriggerComputerMoveAnimation } from './view/MobileStandardChessBoard';
import { triggerComputerMoveAnimation as vintageTriggerComputerMoveAnimation } from './view/VintageChessBoard';
import AboutScreen from './screen/AboutScreen';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import logger from './utils/Logger';
import { PlayerSettings } from './model/PlayerSettings';
import { CircularProgress, Fade } from '@mui/material';
import ReactGA from "react-ga4";
//import { enableDebugLogs } from "./utils/Logger";

const gaMeasurementId = "G-TBPWEH24S2";
const isLiveDomain = window.location.hostname === "captainschess.com";

if (isLiveDomain && gaMeasurementId) {
  ReactGA.initialize(gaMeasurementId);
  ReactGA.send({ hitType: "pageview", page: window.location.pathname });
}

// Enable debug logs to see capture detection logs
//enableDebugLogs();

const MemoizedStandardChessBoard = React.memo(StandardChessBoard);
const MemoizedMobileStandardChessBoard = React.memo(MobileStandardChessBoard);
const MemoizedVintageChessBoard = React.memo(VintageChessBoard);

/** Main App **/
function App() {
  // More reliable mobile detection using User Agent and touch capability
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);

  // Check orientation using media query
  const isPortrait = useMediaQuery('(orientation: portrait)');

  // Initialize mobile detection on component mount
  useEffect(() => {
    // Set the correct viewport height for mobile browsers
    const setViewportHeight = () => {
      // First we get the viewport height and we multiply it by 1% to get a value for a vh unit
      const vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Initial call to set the height
    setViewportHeight();

    // We listen to the resize event
    window.addEventListener('resize', setViewportHeight);
    return () => window.removeEventListener('resize', setViewportHeight);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      // Check if the device has touch capability
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Check user agent for mobile devices
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'iphone', 'ipod', 'ipad', 'windows phone', 'mobile'];

      const isMobileUserAgent = mobileKeywords.some(keyword => userAgent.includes(keyword));

      // Consider a device mobile if it has touch capabilities AND has mobile keywords in UA
      // OR if the screen is narrow (for devices that might not report as mobile)
      const narrowScreen = window.innerWidth < 768;

      setIsMobileDevice(hasTouch && isMobileUserAgent || narrowScreen);

      // Debug information
      logger.debug('Device detection:', {
        hasTouch,
        userAgent,
        isMobileUserAgent,
        narrowScreen,
        result: hasTouch && isMobileUserAgent || narrowScreen
      });
    };

    checkMobile();

    // Also recheck on resize in case of device orientation changes
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use the more reliable isMobileDevice state instead of the media query
  const isMobile = isMobileDevice;

  // State variables for screen visibility
  const [isComputerMoving, setIsComputerMoving] = useState(false);
  const [highlightedSquare, setHighlightedSquare] = useState<Position | null>(null);
  const [highlightedComputerSquare, setHighlightedComputerSquare] = useState<Position | null>(null);
  const [highlightedLegalMoves, setHighlightedLegalMoves] = useState<Position[]>([]);
  // Restore original settings - show title screen at start
  const [showTitleScreen, setShowTitleScreen] = useState(true);
  const [showGameOverScreen, setShowGameOverScreen] = useState(false);
  const [showAboutScreen, setShowAboutScreen] = useState(false);
  const [gameOverReason, setGameOverReason] = useState('');
  const [moveCount, setMoveCount] = useState(0);
  const boardContainerRef = useRef<HTMLDivElement>(null);
  const topLegendRef = useRef<HTMLDivElement>(null);
  const bottomLegendRef = useRef<HTMLDivElement>(null);
  const [boardSize, setBoardSize] = useState<number>(1080);
  const [alertQueue, setAlertQueue] = useState<string[]>([]);
  const computerDirector = useRef<ComputerDirector>(new ComputerDirector());
  const [playerName, setPlayerName] = useState<string>(localStorage.getItem('captainsChess.playerName') || 'Player');
  const [highlightMoves, setHighlightMoves] = useState<boolean>(
    localStorage.getItem('captainsChess.highlightMoves') !== 'false' // Default to true
  );
  const [vintageMode, setVintageMode] = useState<boolean>(
    localStorage.getItem('captainsChess.useVintageBoard') === 'true' || false
  );
  const [openSettings, setOpenSettings] = useState(false);

  const handleGameOver = useCallback((reason: string) => {
    setGameOverReason(reason);
    setShowGameOverScreen(true);
  }, []);

  const initialBoardState = BoardState.initialBoardState();

  // Centralized initializeGame function
  const initializeGame = useCallback(
    (
      boardState: BoardState,
      previousGame: Game | null = null
    ) => {
      let triggerAnimation;

      if (vintageMode) {
        triggerAnimation = vintageTriggerComputerMoveAnimation;
      } else if (isMobile) {
        triggerAnimation = mobileTriggerComputerMoveAnimation;
      } else {
        triggerAnimation = standardTriggerComputerMoveAnimation;
      }

      return new Game(
        boardState,
        previousGame,
        (message: string) => setAlertQueue(prev => [...prev, message]),
        () => {},
        handleGameOver,
        triggerAnimation
      );
    },
    [handleGameOver, vintageMode, isMobile]
  );

  const [game, setGame] = useState(() => initializeGame(initialBoardState));

  useEffect(() => {
    const handleResize = () => {
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;

      // For mobile in landscape mode, legends are on the sides
      if (isMobile && !isPortrait) {
        // Important: Use approximately 80% of the width for the board
        const availableWidth = containerWidth * 0.8;
        const availableHeight = containerHeight - 16; // 16px buffer
        const newBoardSize = Math.min(availableHeight, availableWidth);
        setBoardSize(newBoardSize);
      }
      // For desktop mode - calculate exact size to fill entire available space
      else if (!isMobile) {
        // Exactly the height minus the 100px for legends
        const legendsHeight = 100; // Two 50px legends

        // Calculate available height precisely
        const availableHeight = containerHeight - legendsHeight;

        // Calculate width to maintain aspect ratio without margins
        const newBoardSize = Math.min(containerWidth, availableHeight);

        // Ensure it's divisible by 8 to avoid fractional pixel issues
        const adjustedSize = Math.floor(newBoardSize / 8) * 8;

        setBoardSize(adjustedSize);
      }
      // For mobile in portrait mode
      else {
        // Reserve fixed space for legends (40px each) plus margins and buffer
        const legendsHeight = 100; // Two 40px legends + margins + buffer

        // Calculate available space, but leave more room at the bottom
        const availableHeight = containerHeight - legendsHeight;
        const availableWidth = containerWidth - 16; // 16px buffer

        // Board should be square, so take the minimum dimension
        // Limit to 90% of available height to ensure room for legends
        const newBoardSize = Math.min(availableWidth, availableHeight * 0.9);
        setBoardSize(newBoardSize);
      }
    };

    // Initial sizing
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, isPortrait]);

  const handleStartGame = (settings: PlayerSettings) => {
    // Apply settings
    setPlayerName(settings.playerName);
    setVintageMode(settings.useVintageBoard);
    setHighlightMoves(settings.highlightMoves);

    // Start the game
    setShowTitleScreen(false);
  };

  // Handles the player's move
  const handlePlayerMove = (from: Position, to: Position) => {
    if (game.isGameOver()) {
      return;
    }
    logger.debug(`Player move from ${JSON.stringify(from)} to ${JSON.stringify(to)}`);
    game.handlePlayerMove(from, to);
    setHighlightedSquare(to);
    setTimeout(() => setHighlightedSquare(null), 1000);
    setHighlightedLegalMoves([]);
    setGame(prevGame => initializeGame(prevGame.getBoardState(), prevGame));
  };

  // Handles the computer's move
  useEffect(() => {
    if (!game.isGameOver() && game.getCurrentTurn() === 'Computer' && !isComputerMoving) {
      setIsComputerMoving(true);  // Keep this out the timeout callback so the move state is set immediately, and the `computer thinking` shows
      setTimeout(() => {
        (async () => {
          // Start the computer piece selection and move processing.
          const move = await game.handleComputerMove(); // Await the result
          if (move) {
            setHighlightedComputerSquare(move.to);
            setTimeout(() => setHighlightedComputerSquare(null), 1000);
          }
          setIsComputerMoving(false);
          setGame(prevGame => initializeGame(prevGame.getBoardState(), prevGame));
          setMoveCount(prev => prev + 1);
        })();
      }, 2000); // Introduce a delay before the computer starts moving so the thinking progress meter is initially shown
    }
  }, [game, handleGameOver, isComputerMoving, initializeGame]);

  useEffect(() => {
    if (alertQueue.length > 0) {
      const timer = setTimeout(() => {
        setAlertQueue(prev => prev.slice(1));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [alertQueue]);

  const handleRestart = () => {
    computerDirector.current = new ComputerDirector();
    setGame(initializeGame(initialBoardState));
    setShowGameOverScreen(false);
    setMoveCount(0);
  };

  const handleSettingsOpen = () => {
    setOpenSettings(true);
  };

  const handleSettingsClose = (newSettings?: {highlightMoves: boolean, vintageMode: boolean, playerName: string}) => {
    if (newSettings) {
      setHighlightMoves(newSettings.highlightMoves);
      setVintageMode(newSettings.vintageMode);
      setPlayerName(newSettings.playerName);

      // Save to localStorage
      localStorage.setItem('captainsChess.highlightMoves', String(newSettings.highlightMoves));
      localStorage.setItem('captainsChess.useVintageBoard', String(newSettings.vintageMode));
      localStorage.setItem('captainsChess.playerName', newSettings.playerName);
    }
    setOpenSettings(false);
  };

  const handleViewBoard = () => {
    setShowGameOverScreen(false);
  };

  const handleAboutOpen = () => {
    setShowAboutScreen(true);
  };

  const handleAboutClose = () => {
    setShowAboutScreen(false);
  };

    // Render the appropriate board based on device and settings
  const renderBoard = () => {
    if (vintageMode && !isMobile) {
      return (
        <MemoizedVintageChessBoard
          boardSize={boardSize}
          boardState={game.getBoardState()}
          handlePlayerMoveCallback={handlePlayerMove}
          highlightedSquare={highlightedSquare}
          highlightedComputerSquare={highlightedComputerSquare}
          highlightedLegalMoves={highlightMoves ? highlightedLegalMoves : []}
          setHighlightedLegalMoves={setHighlightedLegalMoves}
          playerColor={{ white: 'white', black: 'black' }}
          currentTurn={game.getCurrentTurn()}
          isGameOver={game.isGameOver()}
        />
      );
    } else if (isMobile) {
      return (
        <MemoizedMobileStandardChessBoard
          boardSize={boardSize}
          boardState={game.getBoardState()}
          handlePlayerMoveCallback={handlePlayerMove}
          highlightedSquare={highlightedSquare}
          highlightedComputerSquare={highlightedComputerSquare}
          highlightedLegalMoves={highlightMoves ? highlightedLegalMoves : []}
          setHighlightedLegalMoves={setHighlightedLegalMoves}
          playerColor={{ white: 'white', black: 'black' }}
          currentTurn={game.getCurrentTurn()}
          isGameOver={game.isGameOver()}
          orientation={isPortrait ? 'portrait' : 'landscape'}
        />
      );
    } else {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            backgroundColor: '#000', // Black background to match the grid
            padding: 0,
            margin: 0,
            position: 'relative',
          }}
        >
          <MemoizedStandardChessBoard
            boardSize={boardSize}
            boardState={game.getBoardState()}
            handlePlayerMoveCallback={handlePlayerMove}
            highlightedSquare={highlightedSquare}
            highlightedComputerSquare={highlightedComputerSquare}
            highlightedLegalMoves={highlightMoves ? highlightedLegalMoves : []}
            setHighlightedLegalMoves={setHighlightedLegalMoves}
            playerColor={{ white: 'white', black: 'black' }}
            currentTurn={game.getCurrentTurn()}
            isGameOver={game.isGameOver()}
          />
        </Box>
      );
    }
  };

  // Common components for all layouts
  const commonComponents = (
    <>
      {/* Conditional positioning for the settings icon based on device and orientation */}
      <IconButton
        onClick={handleSettingsOpen}
        sx={{
          position: 'absolute',
          ...(isMobile && !isPortrait
            ? { bottom: 8, right: 8 } // Mobile landscape: bottom right
            : { top: 8, right: 8 }), // All other modes: top right
          zIndex: 1500,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
      >
        <SettingsIcon sx={{ color: 'white' }} />
      </IconButton>

      <GameSettings
        open={openSettings}
        onClose={handleSettingsClose}
        highlightMoves={highlightMoves}
        vintageMode={vintageMode}
        playerName={playerName}
        isMobile={isMobile}
        isLandscape={!isPortrait && isMobile} // Only consider landscape for mobile devices
      />

      <AboutScreen
        open={showAboutScreen}
        onClose={handleAboutClose}
        _isMobile={isMobile}
        isLandscape={!isPortrait && isMobile}
      />

      <Box sx={{
        position: 'absolute',
        ...(isMobile && !isPortrait
          ? { bottom: 50, right: 8 } // Mobile landscape: move alerts above settings button
          : { top: 8, right: 8 }),  // All other modes: keep at top right
        width: 'auto',
        zIndex: 2000, // Increased from 1000 to 2000 to be above the settings gear (1500)
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        {alertQueue.map((alert, index) => (
          <Alert key={index} severity="info" sx={{ padding: '8px 16px' }}>
            {alert}
          </Alert>
        ))}
      </Box>

      <StartScreen
        open={showTitleScreen}
        onStart={handleStartGame}
        onCredits={handleAboutOpen}
        isMobile={isMobileDevice}
        isLandscape={!isPortrait && isMobileDevice}
      />

      <GameOverScreen
        open={showGameOverScreen}
        reason={gameOverReason}
        onRestart={handleRestart}
        onViewBoard={handleViewBoard}
        onCredits={handleAboutOpen}
        moveCount={moveCount}
        isMobile={isMobileDevice}
        isLandscape={!isPortrait && isMobileDevice}
      />

      {/* Computer thinking indicator - Mobile-friendly non-modal version */}
      <Fade in={isComputerMoving} timeout={{ enter: 300, exit: 200 }}>
        <Box sx={{
          position: 'absolute',
          display: isComputerMoving ? 'flex' : 'none',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px 12px',
          borderRadius: '16px',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1000,
          // Position it centered in the top legend
          top: isMobile ? '20px' : '25px', // Center of 40px/50px height legends
          left: '50%',
          transform: 'translate(-50%, -50%)', // Center horizontally and vertically
        }}>
          <CircularProgress
            size={isMobile ? 16 : 20}
            thickness={5}
            sx={{ color: 'gold', mr: 1 }}
          />
          <Box component="span" sx={{
            color: 'white',
            fontSize: isMobile ? '0.8rem' : '0.95rem',
            fontWeight: 500
          }}>
            Computer thinking...
          </Box>
        </Box>
      </Fade>
    </>
  );

  // Special landscape layout for mobile
  if (isMobile && !isPortrait) {
    return (
      <Grid container spacing={2}>
        {commonComponents}

        {/* Left legend - Computer pieces */}
        <Grid size={{ xs: 12, sm: 1 }}>
          <ComputerCapturedPieces pieces={game.getCapturedPlayerPieces()} isPortrait={isPortrait} isMobile={isMobile} />
        </Grid>

        {/* Center - Chessboard */}
        <Grid size={{ xs: 12, sm: 10 }} ref={boardContainerRef}>
          {renderBoard()}
        </Grid>

        {/* Right legend - Player pieces */}
        <Grid size={{ xs: 12, sm: 1 }}>
          <PlayerCapturedPieces pieces={game.getCapturedComputerPieces()} isPortrait={isPortrait} isMobile={isMobile} />
        </Grid>
      </Grid>
    );
  } else {
    // Desktop or Mobile Portrait mode - treat them differently
    if (isMobile) {
      // Mobile Portrait Mode - optimized for mobile browsers with URL bar
      return (
        <Box
          className="chess-portrait-container"
          sx={{
            height: 'calc(var(--vh, 1vh) * 100)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '0',
            margin: '0',
            overflow: 'hidden'
          }}
        >
          {commonComponents}

          {/* Computer captured pieces on top */}
          <Box
            ref={topLegendRef}
            className="chess-portrait-legend"
            sx={{
              margin: '0',
              padding: '5px 0',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              minHeight: '40px',
              maxHeight: '40px',
              width: '100%'
            }}
          >
            <ComputerCapturedPieces pieces={game.getCapturedPlayerPieces()} isPortrait={isPortrait} isMobile={isMobile} />
          </Box>

          {/* Board in middle - takes all available space */}
          <Box
            className="chess-portrait-board"
            ref={boardContainerRef}
            sx={{
              flex: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0',
              padding: '0',
              width: '100%'
            }}
          >
            {renderBoard()}
          </Box>

          {/* Player captured pieces on bottom */}
          <Box
            ref={bottomLegendRef}
            className="chess-portrait-legend"
            sx={{
              margin: '0',
              padding: '5px 0',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              minHeight: '40px',
              maxHeight: '40px',
              width: '100%'
            }}
          >
            <PlayerCapturedPieces pieces={game.getCapturedComputerPieces()} isPortrait={isPortrait} isMobile={isMobile} />
          </Box>
        </Box>
      );
    } else {
      // Desktop Portrait Mode - fully fill the viewport with no gaps
      return (
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            display: 'grid',
            gridTemplateRows: '50px 1fr 50px',
            gridTemplateColumns: '1fr',
            padding: '0',
            margin: '0',
            overflow: 'hidden',
            backgroundColor: '#000'
          }}
        >
          {commonComponents}

          {/* Computer captured pieces at top */}
          <Box
            ref={topLegendRef}
            sx={{
              width: '100%',
              height: '50px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              gridRow: '1',
              gridColumn: '1'
            }}
          >
            <ComputerCapturedPieces pieces={game.getCapturedPlayerPieces()} isPortrait={isPortrait} isMobile={isMobile} />
          </Box>

          {/* Board container - using grid positioning for precise control */}
          <Box
            ref={boardContainerRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gridRow: '2',
              gridColumn: '1',
              margin: 0,
              padding: 0,
              backgroundColor: '#000'
            }}
          >
            {renderBoard()}
          </Box>

          {/* Player captured pieces at bottom */}
          <Box
            ref={bottomLegendRef}
            sx={{
              width: '100%',
              height: '50px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              gridRow: '3',
              gridColumn: '1'
            }}
          >
            <PlayerCapturedPieces pieces={game.getCapturedComputerPieces()} isPortrait={isPortrait} isMobile={isMobile} />
          </Box>
        </Box>
      );
    }
  }
}

export default App;
